import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { staggerContainer, fadeInUp, tableRowVariants } from '@/lib/animations';
import { useTranslation } from 'react-i18next';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';

function CellContent({ value }: { value: boolean | string }) {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
          <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
        </div>
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-center">
        <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
          <XMarkIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
        </div>
      </div>
    );
  }
  return (
    <span className="text-sm text-gray-600 dark:text-slate-400">{value}</span>
  );
}

export function Comparison() {
  const { t } = useTranslation();
  const { ref, isInView } = useScrollAnimation();

  const comparisonData = [
    {
      feature: t('comparison.features.clientPortal'),
      affogato: true,
      gdrive: false,
      dropbox: false,
    },
    {
      feature: t('comparison.features.consultingStructure'),
      affogato: true,
      gdrive: false,
      dropbox: false,
    },
    {
      feature: t('comparison.features.viewNotifications'),
      affogato: true,
      gdrive: false,
      dropbox: t('comparison.dropboxBusiness'),
    },
    {
      feature: t('comparison.features.methodologyLibrary'),
      affogato: true,
      gdrive: false,
      dropbox: false,
    },
    {
      feature: t('comparison.features.aiDiagnostics'),
      affogato: true,
      gdrive: false,
      dropbox: false,
    },
    {
      feature: t('comparison.features.aiDocuments'),
      affogato: true,
      gdrive: false,
      dropbox: false,
    },
    {
      feature: t('comparison.features.designedForConsultants'),
      affogato: '100%',
      gdrive: '0%',
      dropbox: '0%',
    },
    {
      feature: t('comparison.features.monthlyPrice'),
      affogato: 'Desde $9.99',
      gdrive: '$1.99/100GB',
      dropbox: '$11.99',
    },
  ];

  return (
    <section id="comparison" className="py-16 md:py-24 bg-gray-50 dark:bg-slate-900/50">
      <Container>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Section header */}
          <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              {t('comparison.title')}{' '}
              <span className="text-gradient">{t('comparison.titleHighlight')}</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t('comparison.subtitle')}
            </p>
          </motion.div>

          {/* Comparison table */}
          <motion.div variants={fadeInUp} className="overflow-x-auto">
            <div className="min-w-[600px]">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-lg">
                {/* Header */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('comparison.feature')}
                  </div>
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30">
                      <div className="w-5 h-5 rounded gradient-primary flex items-center justify-center">
                        <span className="text-white text-xs font-bold">A</span>
                      </div>
                      <span className="font-semibold text-primary-700 dark:text-primary-300">
                        Affogato
                      </span>
                    </div>
                  </div>
                  <div className="text-center font-semibold text-gray-600 dark:text-slate-400">
                    Google Drive
                  </div>
                  <div className="text-center font-semibold text-gray-600 dark:text-slate-400">
                    Dropbox
                  </div>
                </div>

                {/* Rows */}
                {comparisonData.map((row, index) => (
                  <motion.div
                    key={index}
                    variants={tableRowVariants}
                    className={`grid grid-cols-4 gap-4 p-4 items-center ${
                      index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-800/50'
                    } ${index !== comparisonData.length - 1 ? 'border-b border-gray-100 dark:border-slate-700/50' : ''}`}
                  >
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {row.feature}
                    </div>
                    <div className="text-center">
                      <CellContent value={row.affogato} />
                    </div>
                    <div className="text-center">
                      <CellContent value={row.gdrive} />
                    </div>
                    <div className="text-center">
                      <CellContent value={row.dropbox} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Bottom note */}
          <motion.div variants={fadeInUp} className="mt-8 text-center">
            <p className="text-sm text-gray-500 dark:text-slate-500">
              {t('comparison.bottomNote')}
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
}
